import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import type { Company, CompanyScope } from "@/api/queries";
import { statusFromParam, useClaimCompany, useCompanies } from "@/api/queries";
import { useMe } from "@/auth/useMe";
import { CompanyTags } from "@/components/CompanyTags";
import { Pagination } from "@/components/Pagination";
import { Timeline } from "@/components/Timeline";
import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  Page,
  Select,
  Spinner,
  StatusBadge,
  Table,
  Tag,
} from "@/components/ui";
import { NuevoSeguimiento } from "@/actividades/NuevoSeguimiento";
import { NuevaEmpresa } from "@/empresas/NuevaEmpresa";
import { useAgents } from "@/leads/queries";
import { formatDate } from "@/lib/format";

/**
 * Screen 02 of the Modernist design — the prospect book (.docs/08 §7 F1).
 *
 * The screen's organising idea is the three ordinal groups: what is mine, what
 * is a colleague's, and what nobody has claimed. They are not a filter and not
 * chrome — they are the information, which is why they survive at 390px as
 * headings over cards when the five-column table cannot.
 *
 * The grouping is *not* computed here. The server sends `assignment_rank` and
 * orders by it, so a page boundary can fall wherever it likes and the headings
 * stay right; working it out in the browser would make page 3 claim it holds
 * "mis empresas" because those happened to be the rows that landed on it.
 */

/** The legacy status integers, for the filter only. 3 was never used. */
const STATUS_OPTIONS = ["1", "2", "4", "5", "6", "7", "8", "9", "10"];

type Group = { rank: number; companies: Company[] };

/**
 * Split a page into its runs of equal rank.
 *
 * A run, not a bucket: the rows arrive sorted, so consecutive-equal is enough
 * and it keeps the server's order intact inside each group.
 */
function groupByRank(companies: Company[]): Group[] {
  const groups: Group[] = [];
  for (const company of companies) {
    const last = groups.at(-1);
    if (last && last.rank === company.assignment_rank) last.companies.push(company);
    else groups.push({ rank: company.assignment_rank, companies: [company] });
  }
  return groups;
}

export function Prospectos() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  // The company a follow-up is being written against, or null. Held as the
  // whole row rather than an id so the modal can name it without a second
  // request for something already on screen.
  const [followUpOn, setFollowUpOn] = useState<Company | null>(null);

  const search = params.get("q") ?? "";
  const status = statusFromParam(params.get("status"));
  const page = Math.max(1, Number(params.get("page") ?? 1) || 1);
  const agent = params.get("agent") ?? "";
  const isManager = me?.is_manager ?? false;

  // The input is local so typing stays responsive; the URL — and therefore the
  // query — only catches up once the seller pauses. At ~26k companies a request
  // per keystroke is both slow and pointless.
  const [draft, setDraft] = useState(search);
  useEffect(() => setDraft(search), [search]);
  useEffect(() => {
    if (draft === search) return;
    const timer = setTimeout(() => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (draft) next.set("q", draft);
          else next.delete("q");
          next.delete("page"); // page 4 of the old search means nothing here
          return next;
        },
        { replace: true },
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [draft, search, setParams]);

  // Always `all`: the three groups *are* the screen. A seller asking for their
  // own book uses the group heading, not a different request.
  const scope: CompanyScope = "all";
  const { data, isPending, isError, isFetching, refetch } = useCompanies({
    search,
    status,
    page,
    scope,
    agent: isManager ? agent : "",
  });
  const agents = useAgents(isManager);
  const claim = useClaimCompany();

  function update(key: string, value: string) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== "page") next.delete("page");
      return next;
    });
  }

  return (
    <Page
      title={t("prospects.title")}
      subtitle={
        data
          ? t("prospects.count", { count: data.count.toLocaleString("es-MX") })
          : undefined
      }
      actions={
        <Button variant="primary" onClick={() => setCreating(true)}>
          {t("prospects.new")}
        </Button>
      }
    >
      <div className="mb-5 flex flex-wrap items-center gap-2.5">
        <Input
          type="search"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t("prospects.searchPlaceholder")}
          aria-label={t("prospects.search")}
          className="min-w-64 flex-1 sm:max-w-[449px] sm:flex-none"
        />
        <Select
          value={status}
          onChange={(event) => update("status", event.target.value)}
          aria-label={t("prospects.filterStatus")}
          className="w-auto"
        >
          <option value="">{t("prospects.allStatuses")}</option>
          {STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {t(`companies.status.${option}`)}
            </option>
          ))}
        </Select>
        {/* The seller picker is manager-only, and says so. An affordance that
            appears for some accounts and not others is read as a bug unless
            the reason is on screen — which is what the design's "Gerencia"
            tag is for. */}
        {isManager ? (
          <div className="flex items-center gap-2">
            <Select
              value={agent}
              onChange={(event) => update("agent", event.target.value)}
              aria-label={t("prospects.filterAgent")}
              className="w-auto"
            >
              <option value="">{t("prospects.allAgents")}</option>
              {(agents.data ?? []).map((option) => (
                <option key={option.id} value={String(option.id)}>
                  {option.name || `#${option.id}`}
                </option>
              ))}
            </Select>
            <Tag
              tone="outline"
              className="text-[10px] tracking-[0.08em] uppercase"
              title={t("prospects.managerOnly")}
            >
              {t("prospects.manager")}
            </Tag>
          </div>
        ) : null}
      </div>

      {search ? (
        <p className="mb-5 text-[13px] text-mute-600">
          {t("prospects.resultsFor")}{" "}
          <span className="font-display font-extrabold text-ink">“{search}”</span>{" "}
          {t("prospects.orderedByAssignment", { count: data?.count ?? 0 })}
        </p>
      ) : null}

      {isPending ? (
        <Spinner label={t("prospects.loading")} />
      ) : isError ? (
        <ErrorState
          message={t("companies.loadFailed")}
          onRetry={() => void refetch()}
        />
      ) : data.results.length === 0 ? (
        <EmptyState>{t("companies.noMatches")}</EmptyState>
      ) : (
        <>
          {groupByRank(data.results).map((group) => (
            <ProspectGroup
              key={group.rank}
              group={group}
              onClaim={(id) => claim.mutate(id)}
              claimingId={claim.isPending ? claim.variables : undefined}
              onFollowUp={setFollowUpOn}
              isManager={isManager}
            />
          ))}
          <Pagination
            page={page}
            count={data.count}
            shown={data.results.length}
            isFetching={isFetching}
            onChange={(next) => update("page", String(next))}
          />
        </>
      )}

      <NuevaEmpresa open={creating} onClose={() => setCreating(false)} />
      <NuevoSeguimiento
        open={followUpOn !== null}
        onClose={() => setFollowUpOn(null)}
        {...(followUpOn
          ? {
              company: followUpOn.id,
              companyName: followUpOn.name || followUpOn.legal_name,
            }
          : {})}
      />
    </Page>
  );
}

/** The ordinal square's colours, per group. Rank 3 is the darkest: unclaimed
 *  work is what the screen most wants a seller to act on. */
const ORDINAL_CLASS: Record<number, string> = {
  1: "bg-brand text-ground",
  2: "bg-mute-400 text-ink",
  3: "bg-ink text-ground",
};

function ProspectGroup({
  group,
  onClaim,
  claimingId,
  onFollowUp,
  isManager,
}: {
  group: Group;
  onClaim: (id: number) => void;
  /** The row with a claim in flight, if any. */
  claimingId: number | undefined;
  onFollowUp: (company: Company) => void;
  isManager: boolean;
}) {
  const { t } = useTranslation();
  const unassigned = group.rank === 3;
  // A colleague's account is a line in this list and nothing more for a
  // seller: its name is not a link and it has no history button, because the
  // server opens neither to them (D25). Their own account they read in full; an
  // unassigned one they may open, and read their own work on; a manager opens
  // and reads everything. This only declines to offer what would be refused.
  const mayOpen = isManager || group.rank !== 2;
  // The rows whose history is open beneath them. A set and not one id: a
  // seller comparing two accounts should not have to close one to read the
  // other. Shared by both renderings, so crossing the breakpoint keeps what
  // was open.
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set());

  function toggle(id: number) {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  return (
    <section className="mb-8">
      <header className="flex flex-wrap items-baseline gap-3.5 border-b-2 border-rule pb-2.5">
        <span
          className={`inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center font-display text-xs font-extrabold ${
            ORDINAL_CLASS[group.rank] ?? ORDINAL_CLASS[2]
          }`}
          aria-hidden="true"
        >
          {group.rank}
        </span>
        <h2 className="text-[17px] sm:text-[19px]">
          {t(`prospects.groupTitle.${group.rank}`)}
        </h2>
        <span className="text-xs text-mute-600">
          {t("prospects.groupCount", { count: group.companies.length })}
        </span>
        <span className="ml-auto text-xs text-mute-600">
          {t(`prospects.groupNote.${group.rank}`)}
        </span>
      </header>

      {/* Two renderings of the same rows, one per width. The table cannot be
          reflowed into the card — five columns and a two-button action cell do
          not survive 390px in any honest form — so .docs/08 §6 draws them as
          separate artboards and they are built as separate markup. */}
      <div className="hidden sm:block">
        <Table>
          <thead>
            <tr>
              <th className="w-[28%]">{t("prospects.column.company")}</th>
              <th className="w-[21%]">{t("prospects.column.contact")}</th>
              <th className="w-[13%]">{t("prospects.column.status")}</th>
              <th className="hidden w-[13%] lg:table-cell">
                {t("prospects.column.lastContact")}
              </th>
              <th className="w-[25%]">
                {unassigned
                  ? t("prospects.column.actions")
                  : t("prospects.column.owner")}
              </th>
              {mayOpen ? (
                <th className="w-px">
                  <span className="sr-only">{t("prospects.column.activities")}</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {group.companies.map((company) => (
              <Fragment key={company.id}>
                <tr className={group.rank === 2 ? "opacity-70" : ""}>
                  <td className="font-semibold">
                    {mayOpen ? (
                      <Link
                        to={`/prospectos/${company.id}`}
                        className="hover:underline"
                      >
                        {company.name || company.legal_name}
                      </Link>
                    ) : (
                      company.name || company.legal_name
                    )}
                    <div className="text-[11px] font-normal text-mute-600">
                      {company.city ?? t("prospects.noCity")}
                    </div>
                  </td>
                  <td>
                    {company.primary_contact?.name ?? t("prospects.noContact")}
                    <div className="text-[11px] text-mute-600">
                      {company.primary_contact?.job_title || ""}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <StatusBadge
                        status={company.status}
                        label={company.status_display}
                      />
                      <CompanyTags company={company} />
                    </div>
                  </td>
                  <td className="hidden text-[13px] text-mute-600 lg:table-cell">
                    {company.last_activity_at
                      ? formatDate(company.last_activity_at)
                      : t("prospects.never")}
                  </td>
                  <td>
                    {unassigned ? (
                      <ClaimActions
                        company={company}
                        onClaim={onClaim}
                        onFollowUp={onFollowUp}
                        busy={claimingId === company.id}
                      />
                    ) : (
                      <span className="text-[13px]">
                        {company.current_agent_name ?? "—"}
                      </span>
                    )}
                  </td>
                  {mayOpen ? (
                    <td className="whitespace-nowrap">
                      <ActivitiesToggle
                        open={expanded.has(company.id)}
                        onToggle={() => toggle(company.id)}
                      />
                    </td>
                  ) : null}
                </tr>
                {/* Its own row, full width and outside the dimming a colleague's
                    row gets: the history is the thing being read. Spanning
                    six while only five show below lg is harmless — the extra
                    span adds no columns and moves none. */}
                {mayOpen && expanded.has(company.id) ? (
                  <tr>
                    <td colSpan={6}>
                      <Timeline scope={{ on: "company", id: company.id }} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="sm:hidden">
        {group.companies.map((company) => (
          <article key={company.id} className="border-b border-rule py-3.5">
            <div className={group.rank === 2 ? "opacity-70" : ""}>
              <div className="flex items-start justify-between gap-2.5">
                <div className="min-w-0">
                  {mayOpen ? (
                    <Link
                      to={`/prospectos/${company.id}`}
                      className="font-display text-[15px] leading-tight font-extrabold"
                    >
                      {company.name || company.legal_name}
                    </Link>
                  ) : (
                    <span className="font-display text-[15px] leading-tight font-extrabold">
                      {company.name || company.legal_name}
                    </span>
                  )}
                  <div className="text-[11px] text-mute-600">
                    {company.city ?? t("prospects.noCity")}
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-1.5">
                  <StatusBadge status={company.status} label={company.status_display} />
                  <CompanyTags company={company} />
                </div>
              </div>

              <div className="mt-2 text-[13px]">
                {company.primary_contact?.name ?? t("prospects.noContact")}
              </div>
              <div className="text-[11px] text-mute-600">
                {company.primary_contact?.job_title || ""}
              </div>

              {unassigned ? (
                <div className="mt-3">
                  <ClaimActions
                    company={company}
                    onClaim={onClaim}
                    onFollowUp={onFollowUp}
                    busy={claimingId === company.id}
                    stacked
                  />
                </div>
              ) : (
                <div className="mt-2.5 flex items-center gap-2 border-t border-rule pt-2.5 text-[11px] tracking-[0.06em] text-mute-600 uppercase">
                  <span>
                    {company.last_activity_at
                      ? formatDate(company.last_activity_at)
                      : t("prospects.never")}
                  </span>
                  <span aria-hidden="true">·</span>
                  <span>{company.current_agent_name ?? "—"}</span>
                </div>
              )}

              {mayOpen ? (
                <div className="mt-2.5">
                  <ActivitiesToggle
                    open={expanded.has(company.id)}
                    onToggle={() => toggle(company.id)}
                    block
                  />
                </div>
              ) : null}
            </div>

            {mayOpen && expanded.has(company.id) ? (
              <div className="mt-3">
                <Timeline scope={{ on: "company", id: company.id }} />
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

/**
 * Opens the company's history in place, under its row — the same timeline the
 * company screen draws, newest first and paged, without leaving the list.
 */
function ActivitiesToggle({
  open,
  onToggle,
  block = false,
}: {
  open: boolean;
  onToggle: () => void;
  /** Full width, for the phone card. */
  block?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Button
      aria-expanded={open}
      block={block}
      className="px-3 py-[5px] text-xs"
      onClick={onToggle}
    >
      {open ? t("prospects.hideActivities") : t("prospects.showActivities")}
    </Button>
  );
}

function ClaimActions({
  company,
  onClaim,
  onFollowUp,
  busy,
  stacked = false,
}: {
  company: Company;
  onClaim: (id: number) => void;
  onFollowUp: (company: Company) => void;
  busy: boolean;
  /** Two full-width columns, as the phone artboard draws them. */
  stacked?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={stacked ? "grid grid-cols-2 gap-2" : "flex gap-1.5"}>
      <Button
        variant="primary"
        className="justify-center px-3 py-[5px] text-xs"
        disabled={busy}
        onClick={() => onClaim(company.id)}
      >
        {busy ? t("prospects.claiming") : t("prospects.claim")}
      </Button>
      <Button
        variant="secondary"
        className="justify-center px-3 py-[5px] text-xs"
        onClick={() => onFollowUp(company)}
      >
        {t("prospects.addFollowUp")}
      </Button>
    </div>
  );
}
